import { VStack } from '@chakra-ui/react';
import {
  Button,
  FieldHelpText,
  Icons,
  InputVariant,
  Notification,
  PassiveVariants,
  SvgSpriteConsumer,
} from '@pluxee-design-system/core';
import { InputControl } from '@pluxee-design-system/react-hook-form';
import { SelectControl } from 'common/forms';
import HtmlText from 'common/typography/HtmlText';
import TerminalRow from 'components/Onboarding/LocationStep/BeTerminalRow';
import { changeAcquirerTerminalIncompatibilityModal } from 'components/Onboarding/modals';
import {
  BelgiumOnboardingValues,
  TerminalAcquirer,
  TerminalAcquirerMidTypeEnum,
  TerminalAcquirerTidTypeEnum,
  TerminalProviders,
} from 'components/Onboarding/types';
import useModal from 'context/ModalContext';
import markupParser from 'html-react-parser';
import { I18nProps, withTranslations } from 'i18n';
import { useCallback, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import gtm from 'trackers/gtm';
import { ConfigField } from 'types/config';
import { template } from './be.utils';

interface TerminalsProps extends I18nProps {
  config: Record<string, ConfigField>;
  disabled?: boolean;
  isOnboarding?: boolean;
  is4cEnabled?: boolean;
  terminalAcquirers: TerminalAcquirer[];
  terminalProviders: TerminalProviders;
}

const Terminals = ({
  config,
  disabled,
  is4cEnabled = true,
  isOnboarding,
  terminalAcquirers,
  terminalProviders,
  t,
  tHtml,
}: TerminalsProps) => {
  const showModal = useModal();
  const {
    append,
    fields: valuesPOS,
    remove,
    replace,
  } = useFieldArray<BelgiumOnboardingValues, 'POS'>({ name: 'POS' });
  const { formState, getValues, setValue, watch } = useFormContext<BelgiumOnboardingValues>();
  const { errors } = formState;
  const valuesTerminalAcquirer = watch('terminalAcquirer');
  const terminalAcquirer = useMemo(
    () => terminalAcquirers.find((a) => a.name === valuesTerminalAcquirer),
    [terminalAcquirers, valuesTerminalAcquirer],
  );
  const numOfTerminals = valuesPOS.length;
  const showMerchantId =
    terminalAcquirer?.mid === TerminalAcquirerMidTypeEnum.OPTIONAL ||
    terminalAcquirer?.mid === TerminalAcquirerMidTypeEnum.MANDATORY;
  const showTerminals =
    terminalAcquirer?.tid === TerminalAcquirerTidTypeEnum.OPTIONAL ||
    terminalAcquirer?.tid === TerminalAcquirerTidTypeEnum.MANDATORY;

  const handleAcquirerChange = useCallback(
    async (newAcquirer: TerminalAcquirer) => {
      const currentTerminals = getValues('POS');
      if (currentTerminals.length > 1 && !newAcquirer.add_additional_terminal) {
        if (
          await showModal(changeAcquirerTerminalIncompatibilityModal(t, tHtml, newAcquirer.label))
        ) {
          replace(currentTerminals.slice(0, 1));
        } else {
          return; // go out
        }
      }
      setValue('terminalAcquirer', newAcquirer.value);
    },
    [getValues, replace, setValue, showModal, t, tHtml],
  );

  const handleAddTerminal = useCallback(() => {
    if (isOnboarding) gtm.trackClickLocationAddTerminal();
    append(template(numOfTerminals.toString()));
  }, [append, numOfTerminals, isOnboarding]);

  return (
    <VStack align="stretch" spacing={4}>
      {is4cEnabled && (
        <SelectControl
          mandatory
          name="terminalAcquirer"
          id="LocationStep_terminalAcquirer"
          placeholder={t('4c_acquirer_whitelist.form.acquirer.placeholder', 'Select one')}
          title={t('4c_acquirer_whitelist.form.acquirer.label', 'Acquirer')}
          notFoundText={t('global_texts.labels.noResults', 'No results matching a query')}
          onChange={handleAcquirerChange}
          options={terminalAcquirers}
          sortAlphabetically={false}
        />
      )}
      {showMerchantId && (
        <InputControl
          type="text"
          mandatory={terminalAcquirer.mid === TerminalAcquirerMidTypeEnum.MANDATORY}
          name="terminalMerchantId"
          id="LocationStep_merchantId"
          placeholder={
            terminalAcquirer?.mid_placeholder ||
            t('4c_acquirer_whitelist.form.merchantId.placeholder', 'Merchant ID')
          }
          title={t('4c_acquirer_whitelist.form.merchantId.label', 'Merchant ID')}
          helpText={terminalAcquirer?.mid_help_text}
          helpTextIcon={<SvgSpriteConsumer svgId={Icons.INFO_OUTLINE16} size={16} />}
        />
      )}
      {terminalAcquirer?.instructions && (
        <Notification
          // @ts-expect-error No error, ReactNode can be passed
          description={
            <HtmlText as="div" variant="body.mediumDemibold">
              {markupParser(terminalAcquirer.instructions)}
            </HtmlText>
          }
          variant={PassiveVariants.PASSIVE_INFORMATION}
          isClosable={false}
          withoutShadow
        />
      )}
      {showTerminals && (
        <>
          <VStack align="stretch" spacing={6}>
            {valuesPOS.map((field, index) => (
              <TerminalRow
                key={`POS-field-${field.id}`}
                config={config}
                idPrefix=""
                name={`POS.${index}.`}
                index={index}
                onRemove={remove}
                providers={terminalProviders}
                canRemove={terminalAcquirer?.add_additional_terminal && numOfTerminals > 1}
                isMandatory={terminalAcquirer?.tid === TerminalAcquirerTidTypeEnum.MANDATORY}
              />
            ))}
          </VStack>
          {(terminalAcquirer?.add_additional_terminal || numOfTerminals === 0) && (
            <Button
              type="button"
              onClick={handleAddTerminal}
              variant="secondaryOutlined"
              id="Terminal_addTerminal"
              size="md"
              alignSelf="flex-start"
            >
              {t('location_add.buttons.addTerminal', 'Add a terminal')}
            </Button>
          )}

          {errors.POS?.message && (
            <FieldHelpText
              disabled={disabled}
              text={errors.POS.message}
              variant={InputVariant.ERROR}
            />
          )}
        </>
      )}
    </VStack>
  );
};

export default withTranslations(Terminals);
